import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faQuestionCircle, faTicket, faBell, faClock, 
    faWrench, faUser, faBook, faCreditCard, 
    faShieldAlt, faFileContract, faSearch, faHome
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';

interface HelpMenuItem {
    id: string;
    title: string;
    description: string;
    icon: any;
    path: string;
    category: 'general' | 'support' | 'legal' | 'system';
    badge?: string;
}

const HelpSupportCenter: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [clinicName, setClinicName] = useState<string>('Facility');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>('all');

    useEffect(() => {
        document.title = "Help & Support Center - Uzima Healthcare";
        loadClinicData();
    }, []);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const helpMenuItems: HelpMenuItem[] = [
        {
            id: 'faqs',
            title: 'Frequently Asked Questions',
            description: 'Find answers to common questions about using the Uzima platform',
            icon: faQuestionCircle,
            path: '/institution/faqs',
            category: 'general'
        },
        {
            id: 'tickets',
            title: 'Support Tickets',
            description: 'Create and manage support tickets for technical issues',
            icon: faTicket,
            path: '/institution/create-ticket',
            category: 'support'
        },
        {
            id: 'notices',
            title: 'System Notices',
            description: 'Important announcements and system updates',
            icon: faBell,
            path: '/institution/notices',
            category: 'system'
        },
        {
            id: 'timeline',
            title: 'Activity Timeline',
            description: 'View your account activity and system events',
            icon: faClock,
            path: '/institution/timelines',
            category: 'system'
        },
        {
            id: 'maintenance',
            title: 'System Maintenance',
            description: 'Scheduled maintenance windows and system status',
            icon: faWrench,
            path: '/institution/maintenance',
            category: 'system'
        },
        {
            id: 'account-help',
            title: 'Account Help',
            description: 'Manage your account settings and profile information',
            icon: faUser,
            path: '/institution/help',
            category: 'general'
        },
        {
            id: 'documentation',
            title: 'Documentation',
            description: 'Comprehensive guides and API documentation',
            icon: faBook,
            path: '/institution/documentation',
            category: 'general'
        },
        {
            id: 'billing-help',
            title: 'Billing Help',
            description: 'Payment, invoicing, and billing support',
            icon: faCreditCard,
            path: '/institution/billing-help',
            category: 'support'
        },
        {
            id: 'privacy',
            title: 'Privacy Policy',
            description: 'How we protect and handle your data',
            icon: faShieldAlt,
            path: '/institution/privacy',
            category: 'legal',
            badge: 'New'
        },
        {
            id: 'terms',
            title: 'Terms & Conditions',
            description: 'Terms of service and user agreements',
            icon: faFileContract,
            path: '/institution/terms',
            category: 'legal',
            badge: 'New'
        }
    ];

    const categories = [
        { id: 'all', name: 'All Categories', count: helpMenuItems.length },
        { id: 'general', name: 'General Help', count: helpMenuItems.filter(item => item.category === 'general').length },
        { id: 'support', name: 'Support', count: helpMenuItems.filter(item => item.category === 'support').length },
        { id: 'system', name: 'System', count: helpMenuItems.filter(item => item.category === 'system').length },
        { id: 'legal', name: 'Legal', count: helpMenuItems.filter(item => item.category === 'legal').length }
    ];

    const filteredItems = helpMenuItems.filter(item => {
        const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const handleItemClick = (path: string) => {
        navigate(path);
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'general': return 'primary';
            case 'support': return 'success';
            case 'system': return 'warning';
            case 'legal': return 'info';
            default: return 'secondary';
        }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h2 className="fw-bold mb-1">
                                            <FontAwesomeIcon icon={faHome} className="me-2 text-primary" />
                                            Help & Support Center
                                        </h2>
                                        <p className="text-muted mb-0">
                                            Get help, find answers, and access support resources for {clinicName}
                                        </p>
                                    </div>
                                    <div className="text-end">
                                        <div className="badge bg-primary fs-6 px-3 py-2">
                                            {helpMenuItems.length} Help Resources
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Search and Filters */}
                        <div className="row mb-4">
                            <div className="col-lg-8">
                                <div className="search-box">
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search help topics, guides, or issues..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <FontAwesomeIcon 
                                            icon={faSearch} 
                                            className="search-icon position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <select
                                    className="form-select"
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                >
                                    {categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name} ({category.count})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="row mb-4">
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <FontAwesomeIcon icon={faQuestionCircle} className="text-primary fs-1 mb-2" />
                                        <h5 className="mb-1">FAQs</h5>
                                        <p className="text-muted mb-0">Common Questions</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <FontAwesomeIcon icon={faTicket} className="text-success fs-1 mb-2" />
                                        <h5 className="mb-1">Support</h5>
                                        <p className="text-muted mb-0">Get Help</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <FontAwesomeIcon icon={faBook} className="text-info fs-1 mb-2" />
                                        <h5 className="mb-1">Guides</h5>
                                        <p className="text-muted mb-0">Documentation</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center">
                                        <FontAwesomeIcon icon={faBell} className="text-warning fs-1 mb-2" />
                                        <h5 className="mb-1">Updates</h5>
                                        <p className="text-muted mb-0">System Notices</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Help Menu Items */}
                        <div className="row">
                            {filteredItems.map((item) => (
                                <div key={item.id} className="col-lg-6 col-xl-4 mb-4">
                                    <div 
                                        className="card border-0 shadow-sm h-100 help-item-card"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleItemClick(item.path)}
                                    >
                                        <div className="card-body">
                                            <div className="d-flex align-items-start">
                                                <div className="flex-shrink-0 me-3">
                                                    <div className={`bg-${getCategoryColor(item.category)} bg-opacity-10 rounded-circle p-3`}>
                                                        <FontAwesomeIcon 
                                                            icon={item.icon} 
                                                            className={`text-${getCategoryColor(item.category)} fs-4`}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <h6 className="card-title mb-0">{item.title}</h6>
                                                        {item.badge && (
                                                            <span className="badge bg-success">{item.badge}</span>
                                                        )}
                                                    </div>
                                                    <p className="card-text text-muted small mb-3">
                                                        {item.description}
                                                    </p>
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <span className={`badge bg-${getCategoryColor(item.category)} bg-opacity-20 text-${getCategoryColor(item.category)}`}>
                                                            {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                                                        </span>
                                                        <small className="text-muted">
                                                            Click to access →
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* No Results */}
                        {filteredItems.length === 0 && (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faSearch} className="text-muted fs-1 mb-3" />
                                <h5 className="text-muted">No help topics found</h5>
                                <p className="text-muted">
                                    Try adjusting your search terms or category filter
                                </p>
                            </div>
                        )}

                        {/* Contact Support */}
                        <div className="row mt-5">
                            <div className="col-12">
                                <div className="card border-0 bg-light">
                                    <div className="card-body text-center py-4">
                                        <h5 className="mb-3">Still need help?</h5>
                                        <p className="text-muted mb-4">
                                            Can't find what you're looking for? Our support team is here to help.
                                        </p>
                                        <div className="d-flex justify-content-center gap-3">
                                            <button 
                                                className="btn btn-primary"
                                                onClick={() => navigate('/institution/create-ticket')}
                                            >
                                                <FontAwesomeIcon icon={faTicket} className="me-2" />
                                                Create Support Ticket
                                            </button>
                                            <button 
                                                className="btn btn-outline-primary"
                                                onClick={() => navigate('/institution/faqs')}
                                            >
                                                <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                                                Browse FAQs
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
        </>
    );
};

export default HelpSupportCenter;
